import React from "react";
import { Overline } from "../../../ForBrands/ui/BrandUI";
import CollapsibleCard from "../../../ForBrands/ui/CollapsibleCard";
import { LegendRow } from "../../../ForBrands/ui/LegendItem";
import { CHART } from "../../../ForBrands/ui/charts/chartTokens";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { RankBadge } from "../../../ForAthletes/v2/profileSections";
import { GAME_RECORD_UNAVAILABLE, NO_GAME_RECORD, snapshotCells } from "./teamModel";
import {
  BAND_LEGEND,
  RETRY_REASONS,
  SNAPSHOT_GROUPS,
  SNAPSHOT_SHAPE,
  descriptiveNote,
  peerLede,
  peerOf,
  snapshotGroups,
} from "./teamPerformanceModel";
import { PendingTile, PerfTile, TileGrid } from "./perf/perfPieces";

// Team Performance Snapshot (BUILD_SPEC B7, Jayson's T1). With the game
// analytics: three groups, Offense (5 tiles), Defense (4) and Net & context
// (Net, Pace, Record, SOS, RPI), each tile with its "Top N%" rank chip and
// band dot (B5; Pace, neither better nor worse, a passive "46th pct" that
// the legend explains), the peer group in the lede and the band legend. While
// they load: the same three groups as placeholders. Without them (a reason,
// or nothing asked): today's league box-score cells from the team style,
// the game-result cells an en dash with a plain note (no record on file,
// or "Not available right now" while the analytics could not load or are
// still being prepared, `reason`).
// Desktop: the V2 collapsible card (~60% of the row beside the archetype);
// phones: the phone card, two tile columns 10px apart.

// Today's cell (the fallback): the value, the label, the filled rank or a note.
const StatCell = ({ cell, loading }) => {
  const pending = loading && cell.boxScore;
  return (
    <div className="flex min-w-0 flex-col items-center gap-2 rounded-[16px] border border-bm-border px-2 py-4 text-center">
      {pending ? <span aria-hidden="true" className="h-[26px] w-14 animate-pulse rounded-[6px] bg-bm-mutedbg" /> : <span className="bm-num-ml text-bm-ink">{cell.value}</span>}
      <Overline>{cell.label}</Overline>
      {cell.rank && <RankBadge chip={{ label: cell.rank }} filled />}
      {cell.note && !pending && <span className="bm-body-s text-bm-tertiary">{cell.note}</span>}
    </div>
  );
};

const GroupHead = ({ label, color }) => (
  <div className="flex items-center gap-2">
    <span aria-hidden="true" className="h-[9px] w-[9px] shrink-0 rounded-[2px]" style={{ background: color }} />
    <h3 className="m-0 bm-overline">{label}</h3>
  </div>
);

const Groups = ({ groups }) => {
  const described = descriptiveNote(groups.flatMap((group) => group.tiles));
  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => (
        <div key={group.key} className="flex flex-col gap-3">
          <GroupHead label={group.label} color={group.color} />
          <TileGrid columns={5} label={group.label}>
            {group.tiles.map((tile) => (
              <PerfTile key={tile.key} tile={tile} band />
            ))}
          </TileGrid>
        </div>
      ))}
      {/* The rank bands, then what Pace's passive chip means when it shows one. */}
      <LegendRow items={[...BAND_LEGEND, described && { key: "descriptive", swatch: "dot", color: CHART.muted, label: described }]} label="Snapshot legend" />
    </div>
  );
};

const PendingGroups = () => (
  <div className="flex flex-col gap-5" aria-busy="true">
    {SNAPSHOT_GROUPS.map((group) => (
      <div key={group.key} className="flex flex-col gap-3">
        <GroupHead label={group.label} color={group.color} />
        <TileGrid columns={5}>
          {Array.from({ length: SNAPSHOT_SHAPE[group.key] }, (_, i) => (
            <PendingTile key={i} />
          ))}
        </TileGrid>
      </div>
    ))}
  </div>
);

// Today's cells from the team style (coach/team/style/). `reason` is why
// the game analytics are missing (the page's answer).
const StyleCells = ({ style, loading, reason }) => {
  const phone = usePhoneTheme();
  const cells = snapshotCells(style, { gameNote: RETRY_REASONS.has(reason) ? GAME_RECORD_UNAVAILABLE : NO_GAME_RECORD });
  const ranked = cells.some((c) => c.rank);
  return (
    <>
      <div className={phone ? "grid grid-cols-2 gap-[10px]" : "grid grid-cols-2 gap-3 sm:grid-cols-4"}>
        {cells.map((cell) => (
          <StatCell key={cell.key} cell={cell} loading={loading} />
        ))}
      </div>
      {ranked && (
        <p className="bm-body-s text-bm-muted">
          Top N% ranks the team among the {style.cohortTeams} teams with box scores in its league{style.season ? `, ${style.season}` : ""}.
        </p>
      )}
    </>
  );
};

// state: "ready" | "loading" | "building" | "unavailable" | "idle"
const TeamSnapshotCard = ({ state = "idle", perf, style, styleLoading = false, collapse }) => {
  const groups = state === "ready" ? snapshotGroups(perf?.payload) : [];
  let body;
  if (state === "ready" && groups.length) body = <Groups groups={groups} />;
  else if (state === "loading" || state === "building") body = <PendingGroups />;
  else body = <StyleCells style={style} loading={styleLoading} reason={perf?.reason} />;
  return (
    <CollapsibleCard title="Team Performance Snapshot" subtitle={state === "ready" && groups.length ? peerLede(peerOf(perf)) : null} {...collapse}>
      {body}
    </CollapsibleCard>
  );
};

export default TeamSnapshotCard;
